import React, { useState } from 'react';
import { Play, Upload, X } from 'lucide-react';
import { Button } from './ui/button';

const VideoPlayer = ({ videoUrl, placeholder, title, description, editable = false }) => {
  const [currentVideo, setCurrentVideo] = useState(videoUrl);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = React.useRef(null);

  // Helper function to get YouTube embed URL
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    
    // YouTube watch URL: https://www.youtube.com/watch?v=VIDEO_ID
    const watchMatch = url.match(/[?&]v=([^&]+)/);
    if (watchMatch) return `https://www.youtube.com/embed/${watchMatch[1]}`;
    
    // YouTube short URL: https://youtu.be/VIDEO_ID
    const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (shortMatch) return `https://www.youtube.com/embed/${shortMatch[1]}`;
    
    // Already an embed URL
    if (url.includes('youtube.com/embed/')) return url;
    
    return null;
  };

  // Helper function to get Vimeo embed URL
  const getVimeoEmbedUrl = (url) => {
    if (!url) return null;
    
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    
    return null;
  };

  // Determine video type
  const youtubeUrl = getYouTubeEmbedUrl(currentVideo);
  const vimeoUrl = getVimeoEmbedUrl(currentVideo);
  const isEmbedVideo = youtubeUrl || vimeoUrl;
  const embedUrl = youtubeUrl || vimeoUrl;

  const handlePlayClick = () => {
    setIsPlaying(true);
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('video/')) {
      const url = URL.createObjectURL(file);
      setCurrentVideo(url);
      setIsPlaying(false);
    }
  };

  return (
    <div className="relative w-full rounded-2xl overflow-hidden bg-slate-900 group">
      {!currentVideo ? (
        // Placeholder with upload option
        <div className="relative aspect-video">
          <img
            src={placeholder}
            alt="Video placeholder"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex items-center justify-center">
            <div className="text-center text-white space-y-4">
              <h3 className="text-2xl font-semibold">{title}</h3>
              <p className="text-sm opacity-90">{description}</p>
              {editable && (
                <div className="flex gap-3 justify-center mt-6">
                  <label htmlFor="video-upload">
                    <Button
                      type="button"
                      size="lg"
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => document.getElementById('video-upload').click()}
                    >
                      <Upload className="w-5 h-5 mr-2" />
                      Upload Video
                    </Button>
                  </label>
                  <input
                    id="video-upload"
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    className="hidden"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      ) : isEmbedVideo ? (
        // YouTube/Vimeo Embed
        <div className="relative aspect-video">
          {!isPlaying ? (
            <div className="relative w-full h-full">
              <img
                src={placeholder}
                alt="Video thumbnail"
                className="w-full h-full object-cover"
              />
              <div
                className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer"
                onClick={handlePlayClick}
              >
                <div className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-300">
                  <Play className="w-10 h-10 text-slate-900 ml-1" fill="currentColor" />
                </div>
              </div>
            </div>
          ) : (
            <iframe
              src={embedUrl}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Video player"
            />
          )}
          {editable && (
            <button
              onClick={() => {
                setCurrentVideo('');
                setIsPlaying(false);
              }}
              className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors z-10"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      ) : (
        // Direct video file
        <div className="relative aspect-video">
          <video
            ref={videoRef}
            src={currentVideo}
            controls
            className="w-full h-full object-cover"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />
          {editable && (
            <button
              onClick={() => {
                setCurrentVideo('');
                setIsPlaying(false);
              }}
              className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
